import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const categoryLinks = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html', icon: 'ri-t-shirt-line' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html', icon: 'ri-t-shirt-line' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html', icon: 'ri-shirt-line' },
  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html', icon: 'ri-pantone-line' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html', icon: 'ri-user-3-line' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/category-917-Sets.html', icon: 'ri-stack-line' },
  { name: 'Underwear/Underpants', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html', icon: 'ri-checkbox-blank-line' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html', icon: 'ri-football-line' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html', icon: 'ri-vip-diamond-line' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/category-921-Bags.html', icon: 'ri-shopping-bag-line' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Bags.html', icon: 'ri-route-line' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Bags.html', icon: 'ri-flask-line' },
  { name: 'OtherStuff', url: 'https://kaspreadsheet.com/search-924/name/Bags.html', icon: 'ri-more-line' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatsOpen, setIsMobileCatsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(target) &&
      dropdownBtnRef.current &&
      !dropdownBtnRef.current.contains(target)
    ) {
      setIsDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, handleClickOutside]);

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-black/30 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        {/* Mobile Layout */}
        <div className="flex md:hidden items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-800 hover:text-primary-600' : 'text-white hover:text-primary-200'
              }`}
            >
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                Home
              </span>
            </Link>
          </div>

          <div className="flex-1 text-center px-2 min-w-0">
            <Link to="/" className="cursor-pointer">
              <span className={`font-heading text-base font-semibold tracking-wide transition-colors truncate block ${
                isScrolled ? 'text-foreground-900' : 'text-white'
              }`}>
                LitBuy Spreadsheets
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                  : 'bg-white text-foreground-900 hover:bg-white/90'
              }`}
            >
              buy it now
            </a>
            <button
              className={`w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${
                isScrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </span>
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between h-18">
          {/* Left: Navigation links */}
          <div className="flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-800 hover:text-primary-600' : 'text-white hover:text-primary-200'
              }`}
            >
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                Home
              </span>
            </Link>
            <Link
              to="/shopping-guide"
              className={`inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-800 hover:text-primary-600' : 'text-white hover:text-primary-200'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Shopping Guide
            </Link>
            <Link
              to="/shopping-guide"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Guides
            </Link>
            <Link
              to="/shopping-guide"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Resources
            </Link>
          </div>

          {/* Center: Brand */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <Link to="/" className="cursor-pointer">
              <span className={`font-heading text-xl font-semibold tracking-wide transition-colors ${
                isScrolled ? 'text-foreground-900' : 'text-white'
              }`}>
                LitBuy Spreadsheets
              </span>
            </Link>
          </div>

          {/* Right: Categories dropdown + CTA buttons */}
          <div className="flex items-center gap-5">
            {/* Categories Dropdown - Click Only */}
            <div className="relative" ref={dropdownRef}>
              <button
                ref={dropdownBtnRef}
                className={`flex items-center gap-1.5 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  isScrolled ? 'text-foreground-800 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
                onClick={toggleDropdown}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
              >
                Categories
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* litbuy spreadsheet button */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex text-sm font-medium px-4 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/25 text-white hover:bg-white/40 backdrop-blur-sm'
              }`}
            >
              litbuy spreadsheet
            </a>

            {/* buy it now button */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold px-5 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                  : 'bg-white text-foreground-900 hover:bg-white/90'
              }`}
            >
              buy it now
            </a>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 pb-4 pt-2 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>
              <Link
                to="/shopping-guide"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>
              <Link
                to="/shopping-guide"
                className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Guides
              </Link>
              <Link
                to="/shopping-guide"
                className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Resources
              </Link>

              <div className="border-t border-background-200/70 my-2"></div>

              {/* Mobile Categories - collapsible */}
              <button
                className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
                onClick={() => setIsMobileCatsOpen(!isMobileCatsOpen)}
              >
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories (14)
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isMobileCatsOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>
              {isMobileCatsOpen && (
                <div className="ml-2 border-l-2 border-background-200/70 pl-2 space-y-0.5">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 rounded-lg cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200/70 my-2"></div>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mx-4 text-center text-sm font-medium bg-primary-500 text-white px-4 py-2.5 rounded-full cursor-pointer whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                litbuy spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mx-4 text-center text-sm font-semibold bg-foreground-900 text-background-50 px-4 py-2.5 rounded-full cursor-pointer whitespace-nowrap mt-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                buy it now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}